import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useDockDialogMotion } from './useDockDialogMotion';
import { useGameScrollLock } from './useGameScrollLock';
import { useModalHistory } from './useModalHistory';

/** One scroll surface, keyboard containment, and focus restoration for game details. */
export const GameDialog: React.FC<{
  title: string;
  titleContent?: React.ReactNode;
  onClose: () => void;
  onEscape?: () => void;
  children: React.ReactNode;
  header?: React.ReactNode;
  variant?: 'log' | 'artwork' | 'location' | 'investigator' | 'allies';
  open?: boolean;
  dockTargetRef?: React.RefObject<HTMLButtonElement | null>;
}> = ({ title, titleContent, onClose, onEscape, children, header, variant, open = true, dockTargetRef }) => {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const visible = useDockDialogMotion(open, dockTargetRef, dialogRef, backdropRef);
  useGameDialogAccessibility(open, visible, dialogRef, dockTargetRef, onClose, false, onEscape);

  if (!visible) return null;
  return createPortal(
    <div ref={backdropRef} aria-hidden={!open || undefined} inert={!open} className={`game-ui game-dialog-backdrop${!open ? ' game-dialog-closing' : ''}${variant ? ` game-${variant}-dialog-backdrop` : ''}`} onMouseDown={event => {
      if (event.target === event.currentTarget) {
        event.preventDefault();
        onClose();
      }
    }}>
      <div className="game-dialog-scrim" aria-hidden="true" />
      <div ref={dialogRef} className={`game-detail-dialog${variant ? ` game-${variant}-dialog` : ''}`} role="dialog" aria-modal={open ? true : undefined} aria-labelledby={titleId}>
        <header className="game-dialog-header">
          <div className="game-dialog-title">
            <h2 id={titleId} aria-label={title}>{titleContent ?? title}</h2>
            <button type="button" className="close-settings game-dialog-close" aria-label={`Close ${title}`} onClick={onClose}>x</button>
          </div>
          {header}
        </header>
        <div className="game-dialog-body">{children}</div>
      </div>
    </div>, document.body
  );
};

export function useGameDialogAccessibility(open: boolean, visible: boolean,
  dialogRef: React.RefObject<HTMLDivElement | null>, dockTargetRef: React.RefObject<HTMLButtonElement | null> | undefined,
  onClose: () => void, restoreToTarget = false, onEscape?: () => void) {
  const closeRef = useRef(onClose);
  closeRef.current = onEscape ?? onClose;
  useGameScrollLock(open && visible);
  useModalHistory(open && visible, onClose);
  useEffect(() => {
    if (!open || !visible) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const dockTarget = dockTargetRef?.current;
    const dialog = dialogRef.current!;
    dialog.querySelector<HTMLButtonElement>('.game-dialog-close')?.focus();
    const handleKey = (event: KeyboardEvent) => {
      const dialogs = document.querySelectorAll('.game-detail-dialog[aria-modal="true"]');
      if (dialogs[dialogs.length - 1] !== dialog) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        closeRef.current();
      }
      if (event.key === 'Tab') {
        const controls = Array.from(dialog.querySelectorAll<HTMLElement>(
          'button:not(:disabled), a[href], select:not(:disabled), input:not(:disabled), [tabindex="0"], summary'
        )).filter(element => !element.closest('[hidden]') && getComputedStyle(element).visibility !== 'hidden' && getComputedStyle(element).display !== 'none' && (!element.closest('details:not([open])') || element.tagName === 'SUMMARY'));
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (event.shiftKey && (document.activeElement === first || !dialog.contains(document.activeElement))) {
          event.preventDefault(); last?.focus();
        } else if (!event.shiftKey && (document.activeElement === last || !dialog.contains(document.activeElement))) {
          event.preventDefault(); first?.focus();
        }
      }
    };
    document.addEventListener('keydown', handleKey, true);
    return () => {
      document.removeEventListener('keydown', handleKey, true);
      const focusTarget = restoreToTarget && dockTarget?.isConnected ? dockTarget : previouslyFocused?.isConnected ? previouslyFocused : dockTarget?.isConnected ? dockTarget : null;
      focusTarget?.focus({ preventScroll: true });
    };
  }, [open, visible, dialogRef, dockTargetRef, restoreToTarget]);
}
